/**
 * Écrans de fin (SPEC-UI §3.7) — version minimale du bac à sable.
 *
 * Les game over et les 3 fins de victoire ont chacun leur écriture (M2.28, M3.9) ; ici on
 * pose ce que le moteur donne déjà : la cause, la chaîne, et de quoi repartir. ⟦A-6⟧ un game
 * over était une feuille froissée ; ⟦C48 · A-168 §3⟧ c'est désormais une `Fenetre` sur le fond de
 * la chambre (`EcranFenetre.tsx`, titre dans le bandeau, boutons dans le pied) : la feuille
 * froissée n'était plus dans la DA pixel de la chambre et des fenêtres.
 *
 * ⟦PLAN §4.13⟧ Depuis que `clore_run` a un effet moteur, cet écran porte aussi le **mémorial
 * de victoire** : la run refermée sur son million (`status === "ended"`). Trois issues s'y
 * croisent, et il faut les distinguer sans jamais reprendre la victoire —
 *
 * | État | Ce qu'on montre |
 * |---|---|
 * | `victory_locked` + `ended` | le mémorial : la plaque, ce que la chaîne a été, l'arbre |
 * | `victory_locked` + `game_over` | la **mort en légende** — la victoire reste intacte |
 * | ni l'un ni l'autre | la fin de chute, et le mémorial hardcore |
 *
 * Le choix « clore ou continuer » lui-même vit dans `Victoire.tsx` : ici, il est déjà fait.
 */

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { estFantome, OBJECTIF } from "@maxime/shared";

import { EcranFenetre } from "../../components/EcranFenetre";
import { useMusique } from "../../components/SoundProvider";
import { PlaqueCanvas } from "../../components/PlaqueCanvas";
import { contenu } from "../../game/content";
import {
  RECITS_DE_FIN,
  RECITS_DE_VICTOIRE,
  TEXTE_CHAINE_REFERMEE,
  texteMortEnLegende,
} from "../../game/fins";
import { BADGE_CLASSE, jourSimule, nombre } from "../../game/format";
import { useRunStore } from "../../stores/runStore";
import { dialog } from "../../stores/uiStore";

import { Fantome } from "./Fantome";
import { arcDeRedemption, tracesDeLaChute } from "./redemption";

/*
 * ⟦C57 · lot 7 · A-174⟧ **Les fins gardent leur narration à l'écran** (Matt, 02/10 : « c'est la
 * récompense »). Le texte de chaque cause vivait ici dans une table `CAUSES` ; il vit maintenant dans
 * `game/fins.ts` (`RECITS_DE_FIN`, mot pour mot) pour que le Journal puisse garder l'épitaphe : une
 * ligne courte, la narration entière derrière « Les dessous » de l'entrée. Les blocs de narration
 * portent `data-narration` : la mesure de conformité les range à part (décision de Matt), tout le
 * reste d'un écran de fin suit la règle. Il n'y a ici aucune explication à déplacer : le score et
 * les deux suites du million vivent sur l'écran de victoire (`Victoire.tsx`).
 *
 * ⟦A-58⟧ §9.4bis : le Fantôme a **son propre écran** (`Fantome.tsx`) ; son entrée de `RECITS_DE_FIN`
 * ne sert qu'à la « mort en légende », où l'écran cite le récit de la cause.
 *
 * ⟦C60 · A-177⟧ **Le grand ménage : il ne reste que deux fins de défaite**, le burn-out terminal et le
 * Fantôme (`gameOversPossibles`, `game/fins.ts`). La faillite, le bannissement et la spirale de
 * réputation ne tombent plus ; leurs récits vivent à part (`RECITS_DE_FIN_ANCIENS`) et ne servent
 * qu'à relire une chaîne d'avant déjà au `game_over`, ce que `RECITS_DE_FIN` (les deux tables
 * réunies) garantit sans un `if` ici. Le mémorial hardcore et la victoire n'ont pas bougé.
 */

export function Fin() {
  const run = useRunStore((s) => s.run);
  const abandonner = useRunStore((s) => s.abandonner);
  const musique = useMusique();
  const naviguer = useNavigate();

  /**
   * ⟦A-71⟧ `memorial_theme` (SPEC-UI §7.6) — et **seulement sur un mémorial**.
   *
   * Cet écran sert trois choses très différentes : le mémorial d'une victoire refermée, la
   * pierre tombale hardcore, et le game over sec du mode normal. Poser un thème sur le
   * troisième serait un contresens — un burn-out terminal n'est pas une commémoration, et
   * §7.6 lui réserve d'ailleurs un autre son (`gameover_sting`, pas encore choisi dans la
   * banque). Le silence est ici la bonne écriture, pas un manque.
   *
   * ⚠️ Calculé **avant** le garde `if (!run)` : un hook ne se saute pas.
   */
  const etat = run?.state ?? null;
  const surUnMemorial =
    etat !== null &&
    (etat.victory_locked ||
      (etat.mode === "hardcore" && etat.game_over !== null && !estFantome(etat)));

  useEffect(() => {
    if (!surUnMemorial) return;
    // En boucle et à volume bas : on relit une chaîne finie, on ne la fête pas.
    musique?.jouer("memorial_theme", { boucle: true, volume: 0.42, fondueMs: 1600 });
    return () => musique?.arreter({ fondueMs: 900 });
  }, [surUnMemorial, musique]);

  if (!run) return null;

  const state = run.state;
  const gagne = state.victory_locked;
  const cause = state.game_over ? RECITS_DE_FIN[state.game_over] : null;
  // ⟦A-58⟧ §9.4bis : « pas de mémorial "Internet t'a oublié" mais son propre écran ».
  const eteinte = estFantome(state);
  // ⟦Hardcore⟧ Une seule vie : la chaîne finit au mémorial (PLAN §4.11, SPEC-MOTEUR §11.6).
  // ⟦A-58⟧ …sauf le Fantôme, à qui §9.4bis retire ce mémorial : il a le sien.
  const memorial = state.mode === "hardcore" && state.game_over !== null && !eteinte;
  const meilleure = [...state.videos].sort((a, b) => b.total_views - a.total_views)[0] ?? null;
  // ⟦A-58⟧ La dernière publiée, pas la meilleure : sur une chaîne éteinte, c'est elle qui est
  // encore en haut de la page quand un visiteur la trouve des années après.
  const derniere = state.videos[state.videos.length - 1] ?? null;
  // ⟦PLAN §4.13⟧ Mourir APRÈS le million ne reprend rien : « mort en légende ».
  const enLegende = gagne && state.game_over !== null;
  // ⟦C62 · A-179 · bug du million⟧ Une victoire gravée dit le million, pas le creux de la nuit qui a suivi le franchissement
  // (voir `Victoire.tsx`). Une mort en légende garde le compteur réel : la chaîne est tombée après.
  const abonnesGraves = gagne && !enLegende ? Math.max(state.subscribers, OBJECTIF) : state.subscribers;
  const noeudsTotal = contenu().skills.length;
  const centPourCent = state.skills.length >= noeudsTotal;
  const fin = state.ending ? RECITS_DE_VICTOIRE[state.ending] : null;

  async function recommencer() {
    // Deux vérités différentes, et on dit la bonne : en bac à sable la run est **effacée** ; côté
    // compte elle reste en base — une run terminée est le mémorial, jamais supprimée (PLAN §4.11,
    // SPEC-API §5.6). Promettre l'effacement dans le second cas serait mentir.
    const ok = await dialog.confirm({
      title: run?.bacASable ? "Effacer cette chaîne ?" : "Repartir sur une nouvelle chaîne ?",
      // ⟦C57 · lot 7⟧ Une phrase par cas (12 mots au plus) : ce qui arrive à la chaîne. « Une chaîne
      // finie ne s'efface pas, elle se relit » reste dit sur l'Historique, où elle se relit.
      body: run?.bacASable
        ? `« ${run.channelName} » disparaît définitivement du bac à sable, et tu repars de zéro.`
        : `« ${run?.channelName} » reste au mémorial : tu repars de zéro à côté.`,
      confirmLabel: "Repartir de zéro",
      cancelLabel: "Rester là",
      danger: true,
    });
    if (!ok) return;
    // ⟦A-97⟧ Même règle qu'aux Réglages : `abandonner()` relance tout sauf le `409
    // run_not_active`, donc un réseau coupé laisse la run en place — le dire vaut mieux que
    // renvoyer sur « nouvelle chaîne » une partie que le serveur tient toujours pour active.
    try {
      await abandonner();
    } catch {
      await dialog.info({
        title: "Le serveur n'a pas confirmé",
        body: "Ta chaîne n'a pas pu être refermée à l'instant. Elle est intacte — réessaie dans un instant.",
        confirmLabel: "Compris",
        icone: "⚠️",
      });
      return;
    }
    naviguer("/nouvelle-run", { replace: true });
  }

  // ⟦C48 · A-168 §3⟧ Les deux boutons vivent dans le PIED de la fenêtre : visibles sans défiler,
  // même sur le mémorial de victoire qui fait 900 px de haut à 1280×800.
  const pied = (
    <>
      <button className="bouton primaire" type="button" onClick={() => void recommencer()}>
        Nouvelle chaîne
      </button>
      {/* ⟦PLAN §4.11 · A-97⟧ « Elle devient un mémorial **consultable** » — et c'est ici que le
          mot compte : le joueur qui vient de perdre est celui qui veut relire ce que sa chaîne a
          été. ⟦A-97 §3⟧ Le bouton vaut maintenant AUSSI en bac à sable : la run y est toujours
          effacée, mais sa fiche est archivée avant de l'être. */}
      <button className="bouton" type="button" onClick={() => naviguer("/historique")}>
        📜 Historique
      </button>
    </>
  );

  // Il y avait ici un « Revoir le studio » : il ne menait nulle part. Le routeur envoie TOUTE
  // route vers cet écran dès que la run est `game_over` ou `ended` — le bouton rechargeait donc
  // la même page. Un bouton qui ne fait rien vaut moins que pas de bouton (même principe que
  // `clore_run` laissé sans bouton tant qu'il était inerte).

  // ⟦A-58⟧ §9.4bis « Le Fantôme » — l'extinction a son écran, pas un titre + un paragraphe comme
  // les quatre accidents. Voir `Fantome.tsx`.
  if (eteinte && !gagne) {
    return (
      <Fantome
        nomChaine={run.channelName}
        abonnes={state.subscribers}
        videosPubliees={state.counters.videos_published}
        clockH={state.clock_h}
        dernierePublicationH={state.counters.last_publication_h}
        derniereVideo={
          derniere ? (contenu().parSujet.get(derniere.subject_id)?.nom ?? derniere.subject_id) : null
        }
        pied={pied}
      />
    );
  }

  if (gagne) {
    return (
      /* ⟦SPEC-UI §3.7 · A-74⟧ À 39/39 nœuds, le mémorial DEVIENT l'écran « plaque dorée » : la
         seule occurrence de `--plaque-or` du jeu cesse d'être une mention entre deux puces —
         c'est le bandeau entier qui se dore. */
      <EcranFenetre
        titre={enLegende ? "Mort en légende" : "Le million"}
        icone="🏆"
        badge={state.mode === "hardcore" ? "hardcore" : undefined}
        large
        className={`fenetre-victoire${centPourCent ? " plaque-doree-ecran" : ""}`}
        pied={pied}
      >
        {/* ⟦SPEC-UI §3.7⟧ Le mémorial de victoire porte la MÊME plaque partageable que l'écran du
            million — c'est le sens du « composant commun » de la spec : la run refermée se relit,
            et on peut encore l'emporter. `plaque-doree` (100 % de l'arbre) reste une distinction
            d'écran : elle ne change pas l'image de partage, qui doit rester la même carte
            1200×630 pour toutes les victoires. */}
        <PlaqueCanvas
          nomChaine={run.channelName}
          abonnes={Math.round(abonnesGraves)}
          jours={jourSimule(state.clock_h)}
          videos={state.counters.videos_published}
          /* Le score vit côté serveur ; cet écran ne le reçoit pas dans l'état. On ne le devine
             pas — la ligne ne sera simplement pas dessinée. */
          score={null}
          rang={null}
          finTitre={fin?.titre ?? null}
          hardcore={state.mode === "hardcore"}
          legende={enLegende}
        />
        <p data-narration="fin">
          {nombre(abonnesGraves)} abonnés au jour {jourSimule(state.clock_h)}. Une rue porte ton
          nom, et quelque part quelqu'un vient de cliquer sur ta première vidéo.
        </p>
        {fin ? <p className="ligne-info">Ta fin : {fin.titre}.</p> : null}
        {enLegende ? (
          <p data-narration="fin">{texteMortEnLegende(state.game_over)}</p>
        ) : (
          <p data-narration="fin">{TEXTE_CHAINE_REFERMEE}</p>
        )}
        {/* ⟦A-20 · A-74⟧ Le mémorial raconte l'arc de rédemption — c'est l'un des deux écrans que
            l'avenant nomme (« surtout sur le mémorial et l'écran de fin »). */}
        {arcDeRedemption(state) ? (
          <p className="memorial-redemption" data-narration="fin">
            On se souvient surtout que cette chaîne a failli s'arrêter — il y a eu{" "}
            {tracesDeLaChute(state).join(", ")}. Elle a gagné quand même. C'est pour ça qu'on la
            relit.
          </p>
        ) : null}
        {centPourCent ? (
          <div className="plaque-doree-banniere">
            <p className="plaque-doree-titre">🏅 La plaque dorée</p>
            <p data-narration="fin">
              Les {noeudsTotal} nœuds de l'arbre, tous pris, après le million. Le village a ressorti
              les barrières une seconde fois — la plaque bleue a désormais une jumelle dorée, et il
              n'en existe qu'une.
            </p>
          </div>
        ) : null}
        <p className="ligne-info discret">
          {state.counters.videos_published} vidéos · arbre {state.skills.length}/{noeudsTotal}{" "}
          nœuds
          {centPourCent ? " · 100 %" : ""}
        </p>
      </EcranFenetre>
    );
  }

  return (
    <EcranFenetre
      titre={cause?.titre ?? "Fin de partie"}
      icone="🪦"
      badge={memorial ? "mémorial hardcore" : undefined}
      className="fenetre-fin"
      pied={pied}
    >
      <p data-narration="fin">{cause?.texte ?? "La chaîne s'arrête là."}</p>
      <p className="ligne-info discret">
        {nombre(state.subscribers)} abonnés · {state.counters.videos_published} vidéos · jour{" "}
        {jourSimule(state.clock_h)}
      </p>

      {/* Le mémorial hardcore : la pierre tombale de SPEC-MOTEUR §11.6. On y grave ce que la
          chaîne a été, pas seulement comment elle est morte. */}
      {memorial ? (
        <div className="pierre-tombale">
          <p className="tombale-ci-git">Ci-gît la chaîne de</p>
          <p className="tombale-nom">{run.channelName}</p>
          <p className="tombale-dates">
            J0 — J{jourSimule(state.clock_h)} · {nombre(state.subscribers)} abonnés
          </p>
          {meilleure ? (
            <p className="tombale-epitaphe" data-narration="fin">
              Sa meilleure : « {contenu().parSujet.get(meilleure.subject_id)?.nom ?? meilleure.subject_id} »
              — {BADGE_CLASSE[meilleure.class].icone} {nombre(meilleure.total_views)} vues.
            </p>
          ) : (
            <p className="tombale-epitaphe" data-narration="fin">
              Elle n'a rien laissé. Ça arrive aussi.
            </p>
          )}
          <p className="tombale-fin">Internet t'a oublié.</p>
        </div>
      ) : null}
    </EcranFenetre>
  );
}
